/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames'; // eslint-disable-line import/named

import { EuiFormControlLayout, EuiValidatableControl } from '../form';
import { EuiErrorBoundary } from '../error_boundary';
import { EuiI18nConsumer } from '../context'; // @ts-ignore the type is provided by react-datepicker.d.ts

import { ReactDatePicker as _ReactDatePicker } from '../../../packages'; // eslint-disable-line import/no-unresolved

export var euiDatePickerDefaultDateFormat = 'MM/DD/YYYY';
export var euiDatePickerDefaultTimeFormat = 'hh:mm A';
var DatePicker = _ReactDatePicker;
export class EuiDatePicker extends Component {
  static defaultProps = {
    adjustDateOnChange: true,
    dateFormat: euiDatePickerDefaultDateFormat,
    fullWidth: false,
    inputRef: () => {},
    isLoading: false,
    shadow: true,
    shouldCloseOnSelect: true,
    showIcon: true,
    showTimeSelect: false,
    timeFormat: euiDatePickerDefaultTimeFormat,
    popoverPlacement: 'bottom-start'
  };

  render() {
    const {
      adjustDateOnChange,
      calendarClassName,
      className,
      customInput,
      dateFormat,
      dayClassName,
      disabled,
      excludeDates,
      filterDate,
      fullWidth,
      iconType,
      injectTimes,
      inline,
      inputRef,
      isInvalid,
      isLoading,
      locale,
      maxDate,
      maxTime,
      minDate,
      minTime,
      onChange,
      onClear,
      openToDate,
      placeholder,
      popperClassName,
      popoverPlacement,
      selected,
      shadow,
      shouldCloseOnSelect,
      showIcon,
      showTimeSelect,
      showTimeSelectOnly,
      timeFormat,
      utcOffset,
      ...rest
    } = this.props;
    const classes = classNames('euiDatePicker', {
      'euiDatePicker--shadow': shadow,
      'euiDatePicker--inline': inline
    });
    const datePickerClasses = classNames('euiDatePicker', 'euiFieldText', {
      'euiFieldText--fullWidth': fullWidth,
      'euiFieldText-isLoading': isLoading,
      'euiFieldText--withIcon': !inline && showIcon,
      'euiFieldText-isInvalid': isInvalid
    }, className);
    let optionalIcon;

    if (inline || customInput || !showIcon) {
      optionalIcon = undefined;
    } else if (iconType) {
      optionalIcon = iconType;
    } else if (showTimeSelectOnly) {
      optionalIcon = 'clock';
    } else {
      optionalIcon = 'calendar';
    } // In case the consumer did not alter the default date format but wants
    // to add the time select, we append the default time format


    let fullDateFormat = dateFormat;

    if (showTimeSelect && dateFormat === euiDatePickerDefaultDateFormat) {
      fullDateFormat = `${dateFormat} ${timeFormat}`;
    } // EuiDatePicker only supports a subset of props from react-datepicker. Using any of
    // the unsupported props below will spit out an error.


    const PropNotSupported = () => {
      throw new Error(`You are using a prop from react-datepicker that EuiDatePicker
        does not support. Please check the EUI documentation for more information.`);
    };

    if ( // We don't want to show multiple months next to each other
    this.props.monthsShown || // There is no need to show week numbers
    this.props.showWeekNumbers || // Our css adapts to height, no need to fix it
    this.props.fixedHeight || // We force the month / year selection UI. No need to configure it
    this.props.dropdownMode || // Short month is unnecessary. Our UI has plenty of room for full months
    this.props.useShortMonthInDropdown || // The today button is not needed. This should always be external to the calendar
    this.props.todayButton || // We hide the time caption, so there is no need to overwrite its text
    this.props.timeCaption || // We always want keyboard accessibility on
    this.props.disabledKeyboardNavigation || // This is easy enough to do. It can conflict with isLoading state
    this.props.isClearable || // There is no reason to launch the datepicker in its own modal. Can always build these ourselves
    this.props.withPortal) {
      return <EuiErrorBoundary>
          <PropNotSupported />
        </EuiErrorBoundary>;
    }

    return <span>
        <span className={classes}>
          <EuiFormControlLayout icon={optionalIcon} fullWidth={fullWidth} clear={selected && onClear ? {
          onClick: onClear
        } : undefined} isLoading={isLoading}>
            <EuiValidatableControl isInvalid={isInvalid}>
              <EuiI18nConsumer>
                {({
                locale: contextLocale
              }) => {
                return <DatePicker adjustDateOnChange={adjustDateOnChange} calendarClassName={calendarClassName} className={datePickerClasses} customInput={customInput} dateFormat={fullDateFormat} dayClassName={dayClassName} disabled={disabled} excludeDates={excludeDates} filterDate={filterDate} injectTimes={injectTimes} inline={inline} locale={locale || contextLocale} maxDate={maxDate} maxTime={maxTime} minDate={minDate} minTime={minTime} onChange={onChange} openToDate={openToDate} placeholderText={placeholder} popperClassName={popperClassName} ref={inputRef} selected={selected} shouldCloseOnSelect={shouldCloseOnSelect} showMonthDropdown showTimeSelect={showTimeSelectOnly ? true : showTimeSelect} showTimeSelectOnly={showTimeSelectOnly} showYearDropdown timeFormat={timeFormat} utcOffset={utcOffset} yearDropdownItemNumber={7} accessibleMode popperPlacement={popoverPlacement} {...rest} />;
              }}
              </EuiI18nConsumer>
            </EuiValidatableControl>
          </EuiFormControlLayout>
        </span>
      </span>;
  }

}
EuiDatePicker.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /**
     * Applies classes to the numbered days provided. Check docs for example.
     */
  dayClassName: PropTypes.func,

  /**
     * Makes the input full width
     */
  fullWidth: PropTypes.bool,

  /**
     * ref for the ReactDatePicker instance
     */
  inputRef: PropTypes.any.isRequired,

  /**
     * Provides styling to the input when invalid
     */
  isInvalid: PropTypes.bool,

  /**
     * Provides styling to the input when loading
     */
  isLoading: PropTypes.bool,

  /**
     * What to do when the input is cleared by the x icon
     */
  onClear: PropTypes.func,

  /**
     * Opens to this date (in moment format) on first press, regardless of selection
     */
  openToDate: PropTypes.any,

  /**
     * Shows only when no date is selected
     */
  placeholder: PropTypes.string,

  /**
     * Can turn the shadow off if using the inline prop
     */
  shadow: PropTypes.bool,

  /**
     * Show the icon in input
     */
  showIcon: PropTypes.bool,

  /**
     * Pass an icon type to change the default `calendar` or `clock` icon
     */
  iconType: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.shape({
    type: PropTypes.any.isRequired,
    side: PropTypes.any,
    ref: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.func.isRequired])
  }).isRequired]),

  /**
     * Sets the placement of the popover. It accepts: `"bottom"`, `"bottom-end"`, `"bottom-start"`, `"left"`, `"left-end"`, `"left-start"`, `"right"`, `"right-end"`, `"right-start"`, `"top"`, `"top-end"`, `"top-start"`
     */
  popoverPlacement: PropTypes.any
};

try {
  EuiDatePicker.__docgenInfo = {
    description: '',
    displayName: 'EuiDatePicker',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: 'Added to the actual input of the calendar',
        name: 'className',
        parent: {
          fileName: 'eui-docs_master/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'eui-docs_master/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'eui-docs_master/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      dayClassName: {
        defaultValue: null,
        description: 'Applies classes to the numbered days provided. Check docs for example.',
        name: 'dayClassName',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        required: false,
        type: {
          name: '(date: Moment) => string'
        }
      },
      fullWidth: {
        defaultValue: {
          value: 'false'
        },
        description: 'Makes the input full width',
        name: 'fullWidth',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      inputRef: {
        defaultValue: {
          value: '() => {}'
        },
        description: 'ref for the ReactDatePicker instance',
        name: 'inputRef',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        required: false,
        type: {
          name: 'Ref<Component<ReactDatePickerProps, any, any>>'
        }
      },
      isInvalid: {
        defaultValue: null,
        description: 'Provides styling to the input when invalid',
        name: 'isInvalid',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isLoading: {
        defaultValue: {
          value: 'false'
        },
        description: 'Provides styling to the input when loading',
        name: 'isLoading',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      onClear: {
        defaultValue: null,
        description: 'What to do when the input is cleared by the x icon',
        name: 'onClear',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        required: false,
        type: {
          name: '(event: MouseEvent<HTMLButtonElement, MouseEvent>) => void'
        }
      },
      openToDate: {
        defaultValue: null,
        description: 'Opens to this date (in moment format) on first press, regardless of selection',
        name: 'openToDate',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        required: false,
        type: {
          name: 'Moment'
        }
      },
      placeholder: {
        defaultValue: null,
        description: 'Shows only when no date is selected',
        name: 'placeholder',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      shadow: {
        defaultValue: {
          value: 'true'
        },
        description: 'Can turn the shadow off if using the inline prop',
        name: 'shadow',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showIcon: {
        defaultValue: {
          value: 'true'
        },
        description: 'Show the icon in input',
        name: 'showIcon',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      iconType: {
        defaultValue: null,
        description: 'Pass an icon type to change the default `calendar` or `clock` icon',
        name: 'iconType',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        required: false,
        type: {
          name: 'string | FunctionComponent<{}> | ComponentClass<{}, any> | (Pick<CommonProps & DisambiguateSet<Pick<ButtonHTMLAttributes<HTMLButtonElement>, "value" | ... 262 more ... | "formTarget">, HTMLAttributes<...>> & HTMLAttributes<...> & { ...; }, "value" | ... 264 more ... | "formTarget"> & { ...; }) | (Pick<...> & { ...; })'
        }
      },
      popoverPlacement: {
        defaultValue: {
          value: 'bottom-start'
        },
        description: 'Sets the placement of the popover. It accepts: `"bottom"`, `"bottom-end"`, `"bottom-start"`, `"left"`, `"left-end"`, `"left-start"`, `"right"`, `"right-end"`, `"right-start"`, `"top"`, `"top-end"`, `"top-start"`',
        name: 'popoverPlacement',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/date_picker.tsx',
          name: 'EuiExtendedDatePickerProps'
        },
        required: false,
        type: {
          name: 'enum',
          raw: 'popperPlacement',
          value: [{
            value: '"left"'
          }, {
            value: '"right"'
          }, {
            value: '"top"'
          }, {
            value: '"bottom"'
          }, {
            value: '"bottom-start"'
          }, {
            value: '"bottom-end"'
          }, {
            value: '"left-end"'
          }, {
            value: '"left-start"'
          }, {
            value: '"right-end"'
          }, {
            value: '"right-start"'
          }, {
            value: '"top-end"'
          }, {
            value: '"top-start"'
          }]
        }
      },
      adjustDateOnChange: {
        defaultValue: {
          value: 'true'
        },
        description: 'Whether changes to Year and Month (via dropdowns) should trigger `onChange`',
        name: 'adjustDateOnChange',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      accessibleMode: {
        defaultValue: null,
        description: '',
        name: 'accessibleMode',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      allowSameDay: {
        defaultValue: null,
        description: '',
        name: 'allowSameDay',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      autoComplete: {
        defaultValue: null,
        description: '',
        name: 'autoComplete',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      autoFocus: {
        defaultValue: null,
        description: '',
        name: 'autoFocus',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      calendarClassName: {
        defaultValue: null,
        description: 'Optional class added to the calendar portion of datepicker',
        name: 'calendarClassName',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      customInput: {
        defaultValue: null,
        description: 'Replaces the input with any node, like a button',
        name: 'customInput',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      customInputRef: {
        defaultValue: null,
        description: '',
        name: 'customInputRef',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      dateFormat: {
        defaultValue: {
          value: null
        },
        description: 'Accepts any moment format string',
        name: 'dateFormat',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'string | string[]'
        }
      },
      dateFormatCalendar: {
        defaultValue: null,
        description: '',
        name: 'dateFormatCalendar',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      disabled: {
        defaultValue: null,
        description: '',
        name: 'disabled',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      disabledKeyboardNavigation: {
        defaultValue: null,
        description: '',
        name: 'disabledKeyboardNavigation',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      dropdownMode: {
        defaultValue: null,
        description: '',
        name: 'dropdownMode',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'enum',
          raw: '"select" | "scroll"',
          value: [{
            value: '"select"'
          }, {
            value: '"scroll"'
          }]
        }
      },
      endDate: {
        defaultValue: null,
        description: '',
        name: 'endDate',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'Moment'
        }
      },
      excludeDates: {
        defaultValue: null,
        description: '',
        name: 'excludeDates',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      excludeTimes: {
        defaultValue: null,
        description: '',
        name: 'excludeTimes',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      filterDate: {
        defaultValue: null,
        description: '',
        name: 'filterDate',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: '(date: Moment) => boolean'
        }
      },
      fixedHeight: {
        defaultValue: null,
        description: '',
        name: 'fixedHeight',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      forceShowMonthNavigation: {
        defaultValue: null,
        description: '',
        name: 'forceShowMonthNavigation',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      formatWeekNumber: {
        defaultValue: null,
        description: '',
        name: 'formatWeekNumber',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: '(date: Moment) => ReactText'
        }
      },
      highlightDates: {
        defaultValue: null,
        description: '',
        name: 'highlightDates',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      includeDates: {
        defaultValue: null,
        description: '',
        name: 'includeDates',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      includeTimes: {
        defaultValue: null,
        description: '',
        name: 'includeTimes',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      inline: {
        defaultValue: null,
        description: '',
        name: 'inline',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      injectTimes: {
        defaultValue: null,
        description: 'Adds additional times to the time selector other then :30 increments',
        name: 'injectTimes',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'Moment[]'
        }
      },
      isClearable: {
        defaultValue: null,
        description: '',
        name: 'isClearable',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      locale: {
        defaultValue: null,
        description: 'Switches the locale / display. "en-us", "zn-ch"...etc',
        name: 'locale',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'LocaleSpecifier'
        }
      },
      maxDate: {
        defaultValue: null,
        description: 'The max date accepted (in moment format) as a selection',
        name: 'maxDate',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'Moment'
        }
      },
      maxTime: {
        defaultValue: null,
        description: 'The max time accepted (in moment format) as a selection',
        name: 'maxTime',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'Moment'
        }
      },
      minDate: {
        defaultValue: null,
        description: 'The min date accepted (in moment format) as a selection',
        name: 'minDate',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'Moment'
        }
      },
      minTime: {
        defaultValue: null,
        description: 'The min time accepted (in moment format) as a selection',
        name: 'minTime',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'Moment'
        }
      },
      monthsShown: {
        defaultValue: null,
        description: '',
        name: 'monthsShown',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'number'
        }
      },
      name: {
        defaultValue: null,
        description: '',
        name: 'name',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      onBlur: {
        defaultValue: null,
        description: '',
        name: 'onBlur',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: '(event: FocusEvent<HTMLInputElement>) => void'
        }
      },
      onChange: {
        defaultValue: null,
        description: 'What to do when the input changes',
        name: 'onChange',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: '(date: Moment, event?: SyntheticEvent<any, Event>) => void'
        }
      },
      onChangeRaw: {
        defaultValue: null,
        description: '',
        name: 'onChangeRaw',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: '(event: FocusEvent<HTMLInputElement>) => void'
        }
      },
      onClickOutside: {
        defaultValue: null,
        description: '',
        name: 'onClickOutside',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: '(event: MouseEvent<HTMLDivElement, MouseEvent>) => void'
        }
      },
      onFocus: {
        defaultValue: null,
        description: '',
        name: 'onFocus',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: '(event: FocusEvent<HTMLInputElement>) => void'
        }
      },
      onKeyDown: {
        defaultValue: null,
        description: '',
        name: 'onKeyDown',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: '(event: KeyboardEvent<HTMLDivElement>) => void'
        }
      },
      onMonthChange: {
        defaultValue: null,
        description: '',
        name: 'onMonthChange',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: '(date: Moment) => void'
        }
      },
      onSelect: {
        defaultValue: null,
        description: '',
        name: 'onSelect',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: '(date: Moment, event?: SyntheticEvent<any, Event>) => void'
        }
      },
      onWeekSelect: {
        defaultValue: null,
        description: '',
        name: 'onWeekSelect',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: '(firstDayOfWeek: Moment, weekNumber: ReactText, event?: SyntheticEvent<any, Event>) => void'
        }
      },
      onYearChange: {
        defaultValue: null,
        description: '',
        name: 'onYearChange',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: '(date: Moment) => void'
        }
      },
      peekNextMonth: {
        defaultValue: null,
        description: '',
        name: 'peekNextMonth',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      placeholderText: {
        defaultValue: null,
        description: '',
        name: 'placeholderText',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      popperClassName: {
        defaultValue: null,
        description: 'Class applied to the popup, when inline is false',
        name: 'popperClassName',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      popperContainer: {
        defaultValue: null,
        description: '',
        name: 'popperContainer',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: '(props: { children: ReactNode[]; }) => ReactNode'
        }
      },
      popperPlacement: {
        defaultValue: null,
        description: '',
        name: 'popperPlacement',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'enum',
          raw: 'popperPlacement',
          value: [{
            value: '"left"'
          }, {
            value: '"right"'
          }, {
            value: '"top"'
          }, {
            value: '"bottom"'
          }, {
            value: '"bottom-start"'
          }, {
            value: '"bottom-end"'
          }, {
            value: '"left-end"'
          }, {
            value: '"left-start"'
          }, {
            value: '"right-end"'
          }, {
            value: '"right-start"'
          }, {
            value: '"top-end"'
          }, {
            value: '"top-start"'
          }]
        }
      },
      preventOpenOnFocus: {
        defaultValue: null,
        description: '',
        name: 'preventOpenOnFocus',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      readOnly: {
        defaultValue: null,
        description: '',
        name: 'readOnly',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      required: {
        defaultValue: null,
        description: '',
        name: 'required',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      scrollableMonthYearDropdown: {
        defaultValue: null,
        description: '',
        name: 'scrollableMonthYearDropdown',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      scrollableYearDropdown: {
        defaultValue: null,
        description: '',
        name: 'scrollableYearDropdown',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      selected: {
        defaultValue: null,
        description: 'The selected datetime (in moment format)',
        name: 'selected',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'Moment'
        }
      },
      selectsEnd: {
        defaultValue: null,
        description: '',
        name: 'selectsEnd',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      selectsStart: {
        defaultValue: null,
        description: '',
        name: 'selectsStart',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      shouldCloseOnSelect: {
        defaultValue: {
          value: 'true'
        },
        description: 'Will close the popup on selection',
        name: 'shouldCloseOnSelect',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showDisabledMonthNavigation: {
        defaultValue: null,
        description: '',
        name: 'showDisabledMonthNavigation',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showMonthDropdown: {
        defaultValue: null,
        description: '',
        name: 'showMonthDropdown',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showMonthYearDropdown: {
        defaultValue: null,
        description: '',
        name: 'showMonthYearDropdown',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showTimeSelect: {
        defaultValue: {
          value: 'false'
        },
        description: 'Show the time selection alongside the calendar',
        name: 'showTimeSelect',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showTimeSelectOnly: {
        defaultValue: null,
        description: 'Only show the time selector, not the calendar',
        name: 'showTimeSelectOnly',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showWeekNumbers: {
        defaultValue: null,
        description: '',
        name: 'showWeekNumbers',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showYearDropdown: {
        defaultValue: null,
        description: '',
        name: 'showYearDropdown',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      startDate: {
        defaultValue: null,
        description: '',
        name: 'startDate',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'Moment'
        }
      },
      startOpen: {
        defaultValue: null,
        description: '',
        name: 'startOpen',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      strictParsing: {
        defaultValue: null,
        description: 'Use Moment strict mode, allowing exact format matches only',
        name: 'strictParsing',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      tabIndex: {
        defaultValue: null,
        description: '',
        name: 'tabIndex',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'number'
        }
      },
      timeCaption: {
        defaultValue: null,
        description: '',
        name: 'timeCaption',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      timeFormat: {
        defaultValue: {
          value: null
        },
        description: 'The format of the time within the selector, in moment notation',
        name: 'timeFormat',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      timeIntervals: {
        defaultValue: null,
        description: '',
        name: 'timeIntervals',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'number'
        }
      },
      title: {
        defaultValue: null,
        description: '',
        name: 'title',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      todayButton: {
        defaultValue: null,
        description: '',
        name: 'todayButton',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      useShortMonthInDropdown: {
        defaultValue: null,
        description: '',
        name: 'useShortMonthInDropdown',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      useWeekdaysShort: {
        defaultValue: null,
        description: '',
        name: 'useWeekdaysShort',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      utcOffset: {
        defaultValue: null,
        description: '',
        name: 'utcOffset',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'number'
        }
      },
      value: {
        defaultValue: null,
        description: '',
        name: 'value',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      weekLabel: {
        defaultValue: null,
        description: '',
        name: 'weekLabel',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      withPortal: {
        defaultValue: null,
        description: '',
        name: 'withPortal',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      yearDropdownItemNumber: {
        defaultValue: null,
        description: '',
        name: 'yearDropdownItemNumber',
        parent: {
          fileName: 'eui-docs_master/src/components/date_picker/react-datepicker.d.ts',
          name: 'ReactDatePickerProps'
        },
        required: false,
        type: {
          name: 'number'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'EuiExtendedDatePickerProps', 'ReactDatePickerProps']
  };
} catch (e) {}